import { createSlice } from '@reduxjs/toolkit';
import { User } from '../models/user';
import { login, getCurrentUser, logout } from './operators';
import { ReduxStatus } from '../../../shared/types';
import { resetAll } from '../../../shared/infra/redux/baseActions';

export interface LoginState {
  user: User | null;
  isAuthenticated: boolean;
  isAdministrator: boolean;
  loginStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  logoutStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getUserStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: LoginState = {
  user: null,
  isAuthenticated: false,
  isAdministrator: false,
  loginStatus: {
    status: 'idle',
    error: null,
  },
  logoutStatus: {
    status: 'idle',
    error: null,
  },
  getUserStatus: {
    status: 'idle',
    error: null,
  },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearAuthStatus: (state) => {
      state.isAuthenticated = false;
      state.isAdministrator = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.loginStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(login.fulfilled, (state) => {
      state.loginStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(login.rejected, (state, { payload }) => {
      state.loginStatus = {
        status: 'failure',
        error: payload ? payload.message : 'Login Error',
      };
    });
    builder.addCase(getCurrentUser.pending, (state) => {
      state.getUserStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCurrentUser.fulfilled, (state, { payload }) => {
      state.getUserStatus = {
        status: 'success',
        error: null,
      };
      state.user = payload;
      state.isAuthenticated = true;
      state.isAdministrator = payload.isAdministratorUser
        ? payload.isAdministratorUser
        : false;
    });
    builder.addCase(getCurrentUser.rejected, (state, { payload }) => {
      state.getUserStatus = {
        status: 'failure',
        error: payload ? payload.message : 'Could not fetch current user',
      };
    });
    builder.addCase(logout.pending, (state) => {
      state.logoutStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.logoutStatus = {
        status: 'success',
        error: null,
      };
      state.isAuthenticated = false;
      state.isAdministrator = false;
      state.user = null;
    });
    builder.addCase(logout.rejected, (state, { payload }) => {
      state.logoutStatus = {
        status: 'failure',
        error: payload ? payload.message : 'Could not logout user',
      };
      state.isAuthenticated = false;
      state.isAdministrator = false;
      state.user = null;
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export const { clearAuthStatus } = userSlice.actions;

export default userSlice.reducer;
